// Library methods
import { useCallback } from "react";
import { DateTime } from "luxon";

// Hooks
import { useAddEditExamHelpers } from "./useAddEditExamHelpers";
import { useLinkExamDevice } from "src/services/devices/hooks/useLinkExamDevice";
import { useAddExam } from "src/services/exams/hooks/useAddExam";

// Constants
import {
  eyeTypeEnums,
  examCardEnums,
  ExamCardSectionNameType,
} from "src/constants/enums";

// Types
import {
  AddExamSectionType,
  AddLensSectionType,
  SelectedExamCardType,
} from "src/services/exams/types";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";
import { useDevicesContext } from "src/contexts/devices/DevicesContext";

export const useAddExams = () => {
  const { LEFT, RIGHT, BOTH } = eyeTypeEnums;

  const { clinicInfo } = useClinicContext();
  const { unlinkedDevices } = useDevicesContext();

  const { mutateAsync: addExamMutation } = useAddExam(clinicInfo?.id);

  const {
    getVfEyeSection,
    getEyeSection,
    getSectionName,
    getLensSimulationSection,
    getVideoSection,
  } = useAddEditExamHelpers();

  const { mutateAsync: linkDeviceMutate } = useLinkExamDevice(clinicInfo?.id);

  const getLensSections = useCallback(
    (selectedExam: SelectedExamCardType) => {
      const { eyeCheckboxes, binocular, sectionOrder } = selectedExam;
      let eyeSection = getLensSimulationSection(BOTH, selectedExam);

      // array for binocular or left/right eye data
      const sections = [];

      if (binocular) {
        sections.push(eyeSection);
      } else {
        if (eyeCheckboxes.left && typeof sectionOrder?.left === "number") {
          sections.push({
            ...eyeSection,
            eye: LEFT,
            order: sectionOrder.left,
          });
        }
        if (eyeCheckboxes.right && typeof sectionOrder?.right === "number") {
          sections.push({
            ...eyeSection,
            eye: RIGHT,
            order: sectionOrder.right,
          });
        }
      }

      return sections;
    },
    [getLensSimulationSection, LEFT, RIGHT, BOTH]
  );

  const getVfSections = useCallback(
    (selectedExam: SelectedExamCardType) => {
      const { eyeCheckboxes, binocular } = selectedExam;

      // array for left and right eye data
      const vfSections = [];

      // handle binocular (Esterman) exam sections
      if (binocular) {
        vfSections.push(getVfEyeSection(BOTH, selectedExam));
      }
      // handle monocular exam sections
      else {
        if (eyeCheckboxes.left) {
          vfSections.push(getVfEyeSection(LEFT, selectedExam));
        }
        if (eyeCheckboxes.right) {
          vfSections.push(getVfEyeSection(RIGHT, selectedExam));
        }
      }

      return vfSections;
    },
    [getVfEyeSection, LEFT, RIGHT, BOTH]
  );

  const getNonVfSections = useCallback(
    (selectedExam: SelectedExamCardType) => {
      const { eyeCheckboxes, binocular, sectionOrder } = selectedExam;
      let eyeSection = getEyeSection(BOTH, selectedExam);

      // array for binocular or left/right eye data
      const sections = [];

      if (binocular) {
        sections.push(eyeSection);
      } else {
        if (eyeCheckboxes.left && typeof sectionOrder?.left === "number") {
          sections.push({
            ...eyeSection,
            eye: LEFT,
            order: sectionOrder.left,
          });
        }
        if (eyeCheckboxes.right && typeof sectionOrder?.right === "number") {
          sections.push({
            ...eyeSection,
            eye: RIGHT,
            order: sectionOrder.right,
          });
        }
      }

      return sections;
    },
    [getEyeSection, BOTH, LEFT, RIGHT]
  );

  const getFormattedExamSections = useCallback(
    (selectedExamCards: Array<SelectedExamCardType>) => {
      let examSections: {
        [key in ExamCardSectionNameType]?: Array<
          AddExamSectionType | AddLensSectionType
        >;
      } = {};

      selectedExamCards.forEach((selectedExam: SelectedExamCardType) => {
        const { examFieldType } = selectedExam;
        const sectionName = getSectionName(selectedExam);

        if (!sectionName) return;

        switch (examFieldType) {
          // videos
          case examCardEnums.VIDEO.algorithmPath:
            const videoSections = getVideoSection(selectedExam);
            examSections[sectionName] = [
              ...(examSections[sectionName] ?? []),
              ...[videoSections],
            ];
            break;

          // lens simulations
          case examCardEnums.IOL_SIMULATION.algorithmPath:
          case examCardEnums.PROGRESSIVE_SIMULATION.algorithmPath:
          case examCardEnums.DRY_EYE_SIMULATION.algorithmPath:
            const lensSections = getLensSections(selectedExam);
            examSections[sectionName] = [
              ...(examSections[sectionName] ?? []),
              ...lensSections,
            ];
            break;

          // visual field exams
          case examCardEnums.VISUAL_FIELD_SCREENING.algorithmPath:
          case examCardEnums.VISUAL_FIELD_THRESHOLDING.algorithmPath:
          case examCardEnums.VISUAL_FIELD_FULL_120.algorithmPath:
          case examCardEnums.VISUAL_FIELD_SUPERIOR_36.algorithmPath:
          case examCardEnums.VISUAL_FIELD_ESTERMAN.algorithmPath:
            const vfSections = getVfSections(selectedExam);
            examSections[sectionName] = [
              ...(examSections[sectionName] ?? []),
              ...vfSections,
            ];
            break;

          // color vision, contrast sensitivity, visual acuity, RAPD
          // Deviation and Correspondence, Fusion and Vergence, Stereopsis
          // Aniseikonia
          default:
            const sections = getNonVfSections(selectedExam);
            examSections[sectionName] = [
              ...(examSections[sectionName] ?? []),
              ...sections,
            ];
        }
      });

      return examSections;
    },
    [
      getSectionName,
      getLensSections,
      getVfSections,
      getNonVfSections,
      getVideoSection,
    ]
  );

  const addNewExam = useCallback(
    async ({
      selectedExamCards,
      scheduledDate,
      patientId,
      deviceId,
    }: {
      selectedExamCards: Array<SelectedExamCardType>;
      scheduledDate: DateTime;
      patientId: string;
      deviceId: string | undefined;
    }) => {
      const examSections = getFormattedExamSections(selectedExamCards);
      const examData = {
        scheduledDate: scheduledDate.toUTC().toISO() ?? "",
        ...examSections,
      };

      const newExam = await addExamMutation({
        patientId,
        examData,
      });

      // if there is only 1 device available, auto-link the exam to it
      if (newExam && unlinkedDevices?.length === 1) {
        await linkDeviceMutate({
          exam: newExam,
          deviceId: unlinkedDevices?.[0]?.id,
        });
      }
      // else there is a selected device, link the exam to it
      else if (newExam && deviceId) {
        await linkDeviceMutate({
          exam: newExam,
          deviceId,
        });
      }
    },
    [
      getFormattedExamSections,
      linkDeviceMutate,
      unlinkedDevices,
      addExamMutation,
    ]
  );

  return {
    addNewExam,
    getFormattedExamSections,
  };
};
